/**
 * Signing-PIN challenge — gates the prescriber signing link.
 *
 * The physician types the 6-digit PIN they set for themselves at onboarding;
 * we hash it and POST the DIGEST to `/api/bridge/magic/verify-identity`. The
 * bridge compares it against the hash IT holds and, on a match, returns an
 * upgraded session token. The completed form's PDF and the submission endpoint
 * both require that upgraded token, so passing here is what actually opens the
 * form. The PIN itself never crosses the wire.
 *
 * THE SERVER DECIDES — deliberately, and from day one. The patient DOB gate was
 * client-side only until 2026-09-05 (case imp_bad9596e): the redeem payload
 * handed the browser the expected hash, `DobChallenge` compared it locally, and
 * "passing" was a React state transition no endpoint ever saw — possession of
 * the link was the entire credential. This component never receives an expected
 * value, never compares anything locally, and fails CLOSED when the check
 * cannot be made. `validatePin` runs first, but only as a courtesy so a
 * mistyped PIN doesn't spend one of the server's attempts; it can only refuse,
 * never admit.
 *
 * A prescriber signing link exposes a completed form's full PHI, so there is NO
 * dev bypass here of any kind — not a prefill, not a flag, not a localhost
 * carve-out. See docs/pilot/MODEL_A_PILOT_PLAN.md §8.
 */

import React, { useState } from "react";

import { clearBridgeSession, sha256Hex, verifyIdentity } from "../../lib/bridgeClient";
import { PIN_LENGTH, validatePin } from "../../lib/signerPin";

interface PinChallengeProps {
  onPass: () => void;
  onFail?: () => void;
}

export default function PinChallenge({ onPass, onFail }: PinChallengeProps) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [burned, setBurned] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(ev?: React.FormEvent<HTMLFormElement>) {
    ev?.preventDefault?.();
    if (burned || busy) return;

    // Courtesy check only — the server re-decides. It exists so a half-typed
    // PIN doesn't burn one of the five server-side attempts.
    const localMsg = validatePin(pin);
    if (localMsg) {
      setError(localMsg);
      return;
    }

    setBusy(true);
    const candidate = await sha256Hex(pin);

    let result: { verified: boolean; attempts_remaining: number; locked_out: boolean };
    try {
      result = await verifyIdentity(candidate);
    } catch {
      // Fail CLOSED: a check that could not be made is not a pass.
      setBusy(false);
      setError(
        "We couldn't check that just now. Please check your connection and try again."
      );
      return;
    }
    setBusy(false);

    if (result.verified) {
      onPass();
      return;
    }

    // Attempts remaining and the lockout both come from the bridge and survive
    // a page reload — a local tally would reset to zero on refresh.
    const left = result.attempts_remaining;
    setError(
      !result.locked_out
        ? `That PIN doesn't match. ${left} attempt${left === 1 ? "" : "s"} left.`
        : "Too many wrong attempts. Contact Parseo Health for a fresh link."
    );
    if (result.locked_out) {
      setBurned(true);
      clearBridgeSession();
      onFail?.();
    }
  }

  const canSubmit = !burned && !busy && pin.length === PIN_LENGTH;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f0eb",
        padding: 24,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          maxWidth: 420,
          width: "100%",
          backgroundColor: "white",
          padding: 32,
          borderRadius: 12,
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <h2 style={{ color: "#182a33", marginTop: 0, marginBottom: 8 }}>
          Enter your signing PIN
        </h2>
        <p style={{ color: "#5b6e7c", marginBottom: 24, lineHeight: 1.45 }}>
          This link opens a patient's completed form for your review and
          signature, so it asks for the {PIN_LENGTH}-digit PIN you set up — the
          text message alone doesn't open it.
        </p>

        <label
          htmlFor="signing-pin"
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            color: "#182a33",
            marginBottom: 6,
          }}
        >
          Signing PIN
        </label>
        <input
          id="signing-pin"
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label="Signing PIN"
          maxLength={PIN_LENGTH}
          autoFocus
          disabled={burned}
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.replace(/\D/g, "").slice(0, PIN_LENGTH));
            if (error) setError(null);
          }}
          style={{
            width: "100%",
            padding: "12px 14px",
            fontSize: 22,
            letterSpacing: "0.35em",
            textAlign: "center",
            border: "1px solid #e6dccb",
            borderRadius: 8,
            backgroundColor: burned ? "#f3f3f3" : "white",
            color: "#182a33",
            boxSizing: "border-box",
          }}
        />

        {error && (
          <p style={{ color: "#c25450", marginTop: 12, marginBottom: 0, fontSize: 14 }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            width: "100%",
            marginTop: 20,
            padding: "12px 14px",
            fontSize: 16,
            fontWeight: 600,
            border: "none",
            borderRadius: 8,
            backgroundColor: canSubmit ? "#182a33" : "#bbb",
            color: "white",
            cursor: canSubmit ? "pointer" : "not-allowed",
          }}
        >
          {busy ? "Checking…" : "Continue"}
        </button>

        <p
          style={{
            fontSize: 12.5,
            color: "#5b6e7c",
            lineHeight: 1.5,
            margin: "16px 0 0",
          }}
        >
          Forgotten your PIN? Contact Parseo Health — we'll verify who you are
          and send a fresh setup link to the same number.
        </p>
      </form>
    </div>
  );
}
